/**
 * 1.8.4 (D-1.8.4-6) — an event on Events, once, as a short card: name · day and time · place · its kind in words ·
 * the numbers that apply · Open (to the event's page, where every press is). Elijah, 10 Oct: "For the Tech Discussion
 * I don't need a volunteer count, Send this invite, Print the list, Desk check-in or RSVP."
 *
 * What applies is asked of the one rule that decides the event's page (`shared/event-cards.ts`, D-1.7.9-3):
 *
 *   RSVP off              no counts, no invite, no print, no check-in, no RSVP list — the name, the time, the place,
 *                         its kind and Open
 *   RSVP on               "12 said yes · 3 said no"; a raffle says where it stands; once the day has come, how many
 *                         checked in
 *   Volunteers asked for  "1 of 4 volunteers"; none asked for: no volunteer count
 *   The event's own day   an event with RSVP has Check-in (the existing check-in sheet) beside Open
 *
 * Used for the List and for a day opened in the Grid. Every number comes with the event list already read; the event's
 * detail is read only when Check-in is pressed.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { checkInOffered, eventSections, shapeOfEvent } from '@shared/event-cards';
import { timeRange } from '@shared/event-month';
import type { EventView } from '@shared/events';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { formatDayKey } from '@/lib/dates';
import { useEvent } from './api';
import { CheckInSheet } from './CheckInSheet';
import '@/styles/ev184.css';

const eid = (id: string) => encodeURIComponent(id);
type Shape = 'calendar' | 'everyone' | 'first' | 'raffle';
const shapeOf = (e: EventView): Shape => (!e.cal.rsvp ? 'calendar' : e.rule === 'raffle' ? 'raffle' : e.rule === 'first' ? 'first' : 'everyone');

/** The numbers that apply to one event, as short lines (each with its own test id). */
function numbersOf(e: EventView, todayKey: string, t: (k: string, o?: Record<string, unknown>) => string): { id: string; icon: 'mail' | 'volunteers' | 'check' | 'events'; text: string }[] {
  const sections = eventSections(shapeOfEvent(e), { canWrite: false });
  const out: { id: string; icon: 'mail' | 'volunteers' | 'check' | 'events'; text: string }[] = [];
  if (e.status === 'Cancelled') return out;
  if (sections.has('rsvpCounts')) {
    out.push({ id: 'rsvp', icon: 'mail', text: t('ev184.cards.rsvp', { yes: e.counts.saidYes, no: e.counts.no }) });
    if (sections.has('draw')) {
      const p = e.three.places;
      out.push({ id: 'raffle', icon: 'events', text: e.raffle?.drawnAt ? t('ev184.cards.raffleDrawn', { given: p.guaranteed + p.winners }) : t('ev184.cards.raffleOpen', { n: p.inDraw }) });
    }
    if (e.dayKey <= todayKey) out.push({ id: 'came', icon: 'check', text: t('ev184.cards.came', { n: e.counts.checkedIn }) });
  }
  if (sections.has('volunteers')) {
    const needed = e.numbers.volunteerPlaces ?? 0;
    const going = e.three.places.volunteersGoing;
    // 17 Oct 2026: no number (volunteers hold a place anyway): "2 volunteers", never "2 of 0"
    out.push({ id: 'volunteers', icon: 'volunteers', text: needed > 0 ? t('ev184.cards.volunteers', { going, needed }) : t('cal177e.helpers.countOnly', { count: going }) });
  }
  return out;
}

export interface ShortCards184Props {
  /** What the list is ("October 2026", or the day opened in the Grid) — its accessible name. */
  what: string;
  events: readonly EventView[];
  todayKey: string;
  /** The administrator and a co-administrator: Check-in on the event's own day. */
  canWrite: boolean;
  /** The heading level of each card's name (3 under the month, 4 under a day). */
  level?: 3 | 4;
}

export function ShortCards184({ what, events, todayKey, canWrite, level = 3 }: ShortCards184Props) {
  const { t, i18n } = useTranslation();
  const H = level === 4 ? 'h4' : 'h3';
  return (
    <ul className="plain-list ev184-cards" aria-label={t('ev184.cards.label', { what })} data-testid="ev184-cards">
      {events.map((e) => {
        const cancelled = e.status === 'Cancelled';
        const time = timeRange(e);
        const shape = shapeOf(e);
        const kind = [e.cal.kind, t(`ev184.kind.${shape}`)].filter(Boolean).join(' · ');
        const numbers = numbersOf(e, todayKey, t);
        // 18 Oct 2026 (D-1018-EV): the one rule, shared with the event's page
        const checkIn = checkInOffered(e, todayKey, { canWrite });
        return (
          <li key={e.eventId} className={`ev184-card${cancelled ? ' is-cancelled' : ''}`} data-testid={`ev184-card-${e.eventId}`}>
            <H className="ev184-name" id={`ev184-h-${e.eventId}`}>
              <span data-testid={`ev184-name-${e.eventId}`}>{e.name}</span>
            </H>
            <p className="ev184-when" data-testid={`ev184-when-${e.eventId}`}>
              {formatDayKey(e.dayKey, i18n.language)}
              {time ? <span className="tnum"> · {time}</span> : null}
            </p>
            {e.place ? (
              <p className="ev184-place" data-testid={`ev184-place-${e.eventId}`}>
                {e.place}
              </p>
            ) : null}
            <p className="ev184-kind" data-testid={`ev184-kind-${e.eventId}`}>
              {kind}
            </p>
            {cancelled ? (
              <p className="ev184-cancelled" data-testid={`ev184-cancelled-${e.eventId}`}>
                <Icon name="x" aria-hidden="true" /> {t('ev184.cards.cancelled')}
              </p>
            ) : null}
            {numbers.length ? (
              <ul className="plain-list ev184-numbers" aria-label={t('ev184.cards.numbers', { name: e.name })} data-testid={`ev184-numbers-${e.eventId}`}>
                {numbers.map((n) => (
                  <li key={n.id} data-testid={`ev184-${n.id}-${e.eventId}`}>
                    <Icon name={n.icon} aria-hidden="true" />
                    <span className="tnum">{n.text}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="ev184-acts">
              <Link className="btn secondary sm" to={`/events/${eid(e.eventId)}`} aria-describedby={`ev184-h-${e.eventId}`} data-testid={`ev184-open-${e.eventId}`}>
                {t('ev184.cards.open')}
              </Link>
              {checkIn ? <CheckIn184 event={e} /> : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** Check-in on the event's own day: the existing sheet, with the event's lines read when it is pressed. */
function CheckIn184({ event }: { event: EventView }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const detail = useEvent(open ? event.eventId : null);
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={(x) => {
          opener.current = x.currentTarget;
          setOpen(true);
        }}
        aria-describedby={`ev184-h-${event.eventId}`}
        data-testid={`ev184-checkin-${event.eventId}`}
      >
        <Icon name="check" /> {t('ev184.cards.checkin')}
      </Button>
      {open && detail.isLoading ? (
        <span className="help" role="status">
          {t('ev184.cards.loading')}
        </span>
      ) : null}
      <Sheet open={open && !!detail.data} onOpenChange={(o) => (!o ? setOpen(false) : undefined)}>
        {open && detail.data ? <CheckInSheet detail={detail.data} onClose={() => setOpen(false)} returnFocusTo={opener} /> : null}
      </Sheet>
    </>
  );
}
